import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CLASS_STYLES, STATUSES, ScoreRing } from "@/lib/match";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, AlertTriangle, ArrowUp, ArrowDown, ChevronsUpDown, Users } from "lucide-react";

const initials = (n) => (n || "?").trim().split(/\s+/).slice(0, 2).map((x) => x[0]).join("").toUpperCase() || "?";

function ClassPill({ value }) {
  if (!value) return <span className="text-muted-foreground text-xs">—</span>;
  const style = CLASS_STYLES[value] || "bg-slate-100 text-slate-600 border-slate-200";
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${style}`}>{value}</span>;
}

export default function Candidates() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fCategory, setFCategory] = useState("all");
  const [fState, setFState] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fDegree, setFDegree] = useState("all");
  const [sort, setSort] = useState({ key: "created", dir: "desc" });
  const navigate = useNavigate();

  useEffect(() => { api.get("/candidates").then((r) => setCandidates(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const categories = useMemo(() => [...new Set(candidates.map((c) => c.primary_classification).filter(Boolean))], [candidates]);
  const states = useMemo(() => [...new Set(candidates.map((c) => c.state).filter(Boolean))], [candidates]);
  const degrees = useMemo(() => [...new Set(candidates.map((c) => c.highest_degree).filter(Boolean))], [candidates]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "name" ? "asc" : "desc" }));

  const filtered = useMemo(() => {
    let list = candidates.filter((c) => {
      const hay = `${c.name} ${c.current_title} ${c.primary_classification} ${(c.skills || []).join(" ")} ${(c.licenses || []).join(" ")} ${(c.languages || []).join(" ")}`.toLowerCase();
      if (q && !hay.includes(q.toLowerCase())) return false;
      if (fCategory !== "all" && c.primary_classification !== fCategory) return false;
      if (fState !== "all" && c.state !== fState) return false;
      if (fStatus !== "all" && c.status !== fStatus) return false;
      if (fDegree !== "all" && c.highest_degree !== fDegree) return false;
      return true;
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    const val = (c) => {
      if (sort.key === "experience") return c.years_experience || 0;
      if (sort.key === "name") return (c.name || "").toLowerCase();
      if (sort.key === "score") return c.top_match_score ?? -1;
      return c.created_at || "";
    };
    return [...list].sort((a, b) => (val(a) < val(b) ? -1 * dir : val(a) > val(b) ? 1 * dir : 0));
  }, [candidates, q, fCategory, fState, fStatus, fDegree, sort]);

  const SortHead = ({ label, k, className = "" }) => {
    const active = sort.key === k;
    const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
    return (
      <th className={`px-4 py-2.5 font-semibold ${className}`}>
        <button data-testid={`sort-${k}`} onClick={() => toggleSort(k)}
          className={`inline-flex items-center gap-1 uppercase tracking-wider text-[11px] transition-colors ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          {label} <Icon size={12} className={active ? "opacity-100" : "opacity-50"} />
        </button>
      </th>
    );
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="mb-6 fade-up">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--teal))] font-semibold">Talent Pool</div>
        <h1 className="font-head text-3xl font-semibold tracking-tight mt-1.5">Candidates</h1>
        <p className="text-muted-foreground text-sm mt-1.5">{filtered.length} of {candidates.length} candidates</p>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-card p-3 mb-5 flex flex-wrap gap-2.5 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="candidate-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, title, skill, license, language…" className="pl-9 h-9 bg-background border-border" />
        </div>
        {[["Category", fCategory, setFCategory, categories], ["State", fState, setFState, states], ["Degree", fDegree, setFDegree, degrees]].map(([label, val, set, opts]) => (
          <Select key={label} value={val} onValueChange={set}>
            <SelectTrigger className="w-[144px] h-9" data-testid={`filter-${label.toLowerCase()}`}><SelectValue placeholder={label} /></SelectTrigger>
            <SelectContent><SelectItem value="all">All {label}s</SelectItem>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        ))}
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-[160px] h-9" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="border border-border rounded-lg bg-card shadow-card overflow-hidden">
          {[...Array(6)].map((_, i) => <div key={i} className="flex items-center gap-4 px-4 py-3.5 border-b border-border last:border-0"><div className="skeleton h-9 w-9 rounded-full" /><div className="flex-1 space-y-1.5"><div className="skeleton h-3 w-40" /><div className="skeleton h-2.5 w-24" /></div><div className="skeleton h-5 w-28 rounded-full" /></div>)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-lg bg-card">
          <Users size={30} className="mx-auto text-muted-foreground/50" />
          <p className="text-muted-foreground mt-3 text-sm">No candidates match. Upload résumés to populate the pool.</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden bg-card shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left border-b border-border">
              <tr>
                <SortHead label="Candidate" k="name" />
                <th className="px-4 py-2.5 font-semibold uppercase tracking-wider text-[11px] text-muted-foreground">Classification</th>
                <th className="px-4 py-2.5 font-semibold uppercase tracking-wider text-[11px] text-muted-foreground hidden lg:table-cell">Degree</th>
                <SortHead label="Exp" k="experience" className="hidden md:table-cell" />
                <th className="px-4 py-2.5 font-semibold uppercase tracking-wider text-[11px] text-muted-foreground hidden xl:table-cell">State</th>
                <th className="px-4 py-2.5 font-semibold uppercase tracking-wider text-[11px] text-muted-foreground hidden md:table-cell">Status</th>
                <SortHead label="Top Match" k="score" className="text-center" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`candidate-row-${c.id}`} onClick={() => navigate(`/candidates/${c.id}`)}
                  className="border-b border-border last:border-0 hover:bg-secondary/40 cursor-pointer transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="h-9 w-9 shrink-0 rounded-full bg-[hsl(var(--teal))]/10 text-[hsl(var(--teal))] flex items-center justify-center text-xs font-semibold font-head">{initials(c.name)}</span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 font-medium text-foreground truncate">
                          {c.name || "Unnamed"}
                          {c.duplicate_flag && <span title="Possible duplicate"><AlertTriangle size={13} className="text-[hsl(var(--gold))]" /></span>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{c.current_title || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><ClassPill value={c.primary_classification} /></td>
                  <td className="px-4 py-3 text-foreground/75 hidden lg:table-cell">{c.highest_degree || "—"}</td>
                  <td className="px-4 py-3 tabular-nums text-foreground/75 hidden md:table-cell">{c.years_experience || 0}y</td>
                  <td className="px-4 py-3 text-foreground/75 hidden xl:table-cell">{c.state || "—"}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium capitalize text-foreground/70">{(c.status || "new").replace(/_/g, " ")}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-center">
                      {typeof c.top_match_score === "number" ? <ScoreRing score={c.top_match_score} size={38} /> : <span className="text-xs text-muted-foreground">—</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
